import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Container, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { useAuth } from './AuthContext';
import { TopNavBar } from './shared/TopNavBar';
import { GuardCalendar } from './guard/GuardCalendar';
import { GuardMessageCard } from './guard/GuardMessageCard';
import { GUARD_MIN_DATE, clampGuardDate, extractGuardApiErrorMessage, fetchGuardDaily, formatGuardDate } from './guard/guardApi';
import { GuardDailyItem, GuardDailyResponse } from './guard/types';

const GuardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const maxAvailableDate = useMemo(() => new Date(), []);
  const [selectedDate, setSelectedDate] = useState(() => clampGuardDate(new Date(), GUARD_MIN_DATE, maxAvailableDate));
  const [items, setItems] = useState<GuardDailyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const responseCacheRef = useRef<Map<string, GuardDailyResponse>>(new Map());
  const hasGuardAccess = Boolean(user?.guard_has_access);

  const selectedDateKey = useMemo(() => formatGuardDate(selectedDate), [selectedDate]);

  useEffect(() => {
    if (authLoading) {
      return;
    }
    if (!hasGuardAccess) {
      setItems([]);
      setError(null);
      setLoading(false);
      return;
    }

    const cached = responseCacheRef.current.get(selectedDateKey);
    if (cached) {
      setItems(cached.items);
      setError(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchGuardDaily(selectedDateKey, controller.signal);
        if (!cancelled) {
          responseCacheRef.current.set(selectedDateKey, response);
          setItems(response.items);
        }
      } catch (err: any) {
        if (!cancelled && err?.name !== 'AbortError') {
          setItems([]);
          setError(extractGuardApiErrorMessage(err));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [authLoading, hasGuardAccess, selectedDateKey]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background:
          'radial-gradient(circle at top, rgba(191, 219, 254, 0.72) 0%, rgba(239, 246, 255, 0.88) 36%, #eef5ff 100%)',
      }}
    >
      <TopNavBar />

      <Container maxWidth={false} sx={{ px: { xs: 2, md: 4 }, py: 4 }}>
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '34px',
            p: { xs: 2, md: 3.5 },
            border: '1px solid rgba(147, 197, 253, 0.38)',
            background: 'linear-gradient(180deg, rgba(255,255,255,0.78) 0%, rgba(248,251,255,0.92) 100%)',
            backdropFilter: 'blur(22px)',
            boxShadow: '0 35px 70px rgba(37, 99, 235, 0.1)',
          }}
        >
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems="flex-start">
            <Box sx={{ width: { xs: '100%', lg: 250 }, flexShrink: 0 }}>
              <Typography sx={{ fontSize: '1.2rem', fontWeight: 900, color: '#0f172a', mb: 2 }}>
                守卫
              </Typography>
              <Typography
                sx={{
                  color: '#64748b',
                  fontSize: '0.92rem',
                  lineHeight: 1.75,
                  mb: 2.5,
                }}
              >
                点击日历不同日期，实时关注AI大事
              </Typography>
              {hasGuardAccess && (
                <GuardCalendar
                  selectedDate={selectedDate}
                  onSelectDate={(date) => setSelectedDate(clampGuardDate(date, GUARD_MIN_DATE, maxAvailableDate))}
                  minDate={GUARD_MIN_DATE}
                  maxDate={maxAvailableDate}
                />
              )}
            </Box>

            <Box sx={{ flex: 1, width: '100%' }}>
              <Box
                sx={{
                  borderRadius: '28px',
                  border: '1px solid rgba(147, 197, 253, 0.3)',
                  background: 'rgba(255,255,255,0.62)',
                  minHeight: 620,
                  p: { xs: 2, md: 3 },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, mb: 3 }}>
                  <Box>
                    <Typography sx={{ fontSize: '1.18rem', fontWeight: 800, color: '#0f172a' }}>
                      {selectedDate.getMonth() + 1}月{selectedDate.getDate()}日消息
                    </Typography>
                  </Box>
                  {!loading && !error && (
                    <Typography sx={{ color: '#2563eb', fontWeight: 800 }}>
                      {items.length} 条
                    </Typography>
                  )}
                </Box>

                {authLoading || loading ? (
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    spacing={1.5}
                    sx={{ minHeight: 360, color: '#2563eb' }}
                  >
                    <CircularProgress size={28} />
                    <Typography sx={{ color: '#475569', fontWeight: 700 }}>加载中...</Typography>
                  </Stack>
                ) : !hasGuardAccess ? (
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    spacing={2}
                    sx={{ minHeight: 360, textAlign: 'center' }}
                  >
                    <Typography sx={{ fontSize: '1.35rem', fontWeight: 900, color: '#0f172a' }}>
                      守卫内容已锁定
                    </Typography>
                    <Typography sx={{ color: '#64748b', maxWidth: 420, lineHeight: 1.9 }}>
                      开通守卫月卡后，可按日期查看每日 AI 大事整理内容
                    </Typography>
                    <Typography sx={{ color: '#2563eb', fontWeight: 800 }}>
                      10 积分 / 30 天
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => navigate('/profile#guard-membership')}
                      sx={{ borderRadius: '999px', px: 3, py: 1.1, fontWeight: 800, boxShadow: 'none' }}
                    >
                      前往个人中心购买
                    </Button>
                  </Stack>
                ) : error ? (
                  <Alert severity="error" sx={{ borderRadius: '18px' }}>
                    {error}
                  </Alert>
                ) : items.length === 0 ? (
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    spacing={1}
                    sx={{ minHeight: 360 }}
                  >
                    <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a' }}>
                      当天暂无消息
                    </Typography>
                    <Typography sx={{ color: '#64748b' }}>
                      可以切换左侧日期查看其他抓取日。
                    </Typography>
                  </Stack>
                ) : (
                  <Stack spacing={2.25}>
                    {items.map((item, index) => (
                      <GuardMessageCard key={item.id} item={item} index={index} />
                    ))}
                  </Stack>
                )}
              </Box>
            </Box>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default GuardPage;
